import { useCallback, useMemo } from 'react';
import gravatarUrl from 'gravatar-url';
import { Avatar, Box, styled, useTheme } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { AppMode, AppModes, useAppMode, useUser } from '@tymely/services';
import { NavMenu } from '@tymely/components';

import SideBarItem from './SideBarItem';

const ModeNavMenuItem = styled(NavMenu)`
    width: ${({ theme }) => theme.spacing(30.5)};
    max-height: fit-content;
    padding-left: ${({ theme }) => theme.spacing(3)};
`;

const TicketHandlingModeSelector = () => {
    const theme = useTheme();
    const user = useUser();
    const { appMode, switchMode, isChat } = useAppMode();
    const colors = useMemo(() => ['#8DFEC8', '#9BB1FF', '#EB9BFF', '#FE7CA3', '#EECD77', '#FFF'], []);

    const options = useMemo(
        () =>
            Object.entries(AppModes).map(([value, label], index) => ({
                label,
                value,
                color: colors[index],
            })),
        [],
    );
    const option = useMemo(() => options.find((opt) => opt.value === appMode), [appMode, options]);
    const onSelect = useCallback(
        (value: AppMode) => {
            switchMode(value);
        },
        [switchMode],
    );

    const avatarSrc = useMemo(
        () =>
            gravatarUrl(user?.email || '', {
                size: 200,
                default: 'mm',
            }),
        [user],
    );

    return (
        <SideBarItem
            title={user?.username}
            icon={
                <Box sx={{ position: 'relative', width: 'auto' }}>
                    <Avatar
                        src={avatarSrc}
                        sx={{
                            height: theme.spacing(6),
                            width: theme.spacing(6),
                            background: theme.palette.secondary.main,
                        }}
                    />
                    {appMode && (
                        <CircleIcon
                            sx={{
                                fontSize: 16,
                                position: 'absolute',
                                right: theme.spacing(-0.25),
                                bottom: theme.spacing(-0.25),
                                color: option?.color,
                            }}
                        />
                    )}
                </Box>
            }
            hasMenu={!!appMode}
            disabled={isChat}
        >
            {appMode
                ? (anchorEl, open, setClose) => (
                      <ModeNavMenuItem
                          anchorEl={anchorEl}
                          open={open}
                          value={appMode}
                          options={options}
                          onSelect={(value) => {
                              onSelect(value as AppMode);
                              setClose();
                          }}
                          onClose={setClose}
                      />
                  )
                : undefined}
        </SideBarItem>
    );
};

TicketHandlingModeSelector.displayName = 'TicketHandlingModeSelector';
export default TicketHandlingModeSelector;
